import { z } from 'zod';

export const SimulationStepSchema = z.object({
  tick: z.number().int().nonnegative(),
  tag_values: z.array(z.tuple([z.string(), z.string()])),
});

export const SimulationRunSchema = z.object({
  program_id: z.string().min(1),
  steps: z.array(SimulationStepSchema),
  final_state: z.array(z.tuple([z.string(), z.string()])),
});

export type ParsedSimulationRun = z.infer<typeof SimulationRunSchema>;

// =====================================================================
// P15.B2 — /api/sim/run request schema.
//
// Drives a fresh WasmRuntime in cloud-api: applies a CBOR-encoded
// RuntimeProject blob, starts a named sequence with params, then ticks
// the simulator clock and streams RunEvents back over SSE.
//
// `project_cbor_base64` is a `RuntimeProject` serialized as CBOR
// (matching the same blob the master board accepts via PushFull) then
// base64-encoded for JSON transport.
//
// `sensor_scripts` lets callers pre-load each sensor's FIFO read queue
// — keys are sensor_ids, values are arrays of TagValue-shaped JSON
// objects (e.g. `[{"F64": 12.5}, {"Bool": true}]`).
// =====================================================================

export const SimRunRequestSchema = z.object({
  project_cbor_base64: z.string().min(1),
  sequence_name: z.string().min(1),
  params: z.record(z.string(), z.unknown()).default({}),
  max_ticks: z.number().int().min(1).max(100000).default(1000),
  tick_step_ms: z.number().int().min(1).max(60000).default(100),
  sensor_scripts: z.record(z.string(), z.array(z.unknown())).optional(),
  user_id: z.string().min(1).default('sim-user'),
});
export type ParsedSimRunRequest = z.infer<typeof SimRunRequestSchema>;

// =====================================================================
// P15.D — /api/sim/run-from-project request schema.
//
// Same simulator entrypoint as /api/sim/run, but the caller identifies a
// stored Firestore project instead of supplying a pre-encoded CBOR blob.
// cloud-api walks /workspaces/{wid}/projects/{pid}/{actions,sequences,…},
// assembles the RuntimeProject envelope, and CBOR-encodes server-side
// before driving the WasmRuntime.
// =====================================================================

export const SimRunFromProjectRequestSchema = z.object({
  workspace_id: z.string().min(1),
  project_id: z.string().min(1),
  sequence_name: z.string().min(1),
  params: z.record(z.string(), z.unknown()).default({}),
  max_ticks: z.number().int().min(1).max(100000).default(1000),
  tick_step_ms: z.number().int().min(1).max(60000).default(100),
  sensor_scripts: z.record(z.string(), z.array(z.unknown())).optional(),
  user_id: z.string().min(1).default('sim-user'),
});
export type ParsedSimRunFromProjectRequest = z.infer<typeof SimRunFromProjectRequestSchema>;
